import html2pdf from 'html2pdf.js';

/**
 * Service de génération de PDF pour les documents légaux
 */

/**
 * Génère le HTML formaté pour un document légal
 * @param {Object} document - Le document légal
 * @param {Object} client - Les informations du client
 * @returns {string} HTML formaté
 */
export const genererHTMLDocumentLegal = (document, client) => {
  const companyInfo = document.companyInfo || {
    name: 'OMAD Services',
    address: '30 bis Rue des Bondoires, 18230 Saint-Doulchard',
    phone: '06 67 72 73 30',
    email: 'contact@omadservices.com',
    siret: '',
    rcs: 'RCS Bourges n° 922 592 662',
    capital: '500,00 €'
  };

  const getDocumentTypeLabel = (type) => {
    const types = {
      'MANDAT': 'Contrat de Prestation de Mandat',
      'CGS': 'Conditions Générales de Service',
      'CONTRAT_TRAVAIL': 'Contrat de Travail',
      'REGLEMENT_BENEFICIAIRES': 'Règlement Intérieur Bénéficiaires',
      'REGLEMENT_INTERVENANTS': 'Règlement Intérieur Intervenants',
      'ATTESTATION_FISCALE': 'Attestation Fiscale Annuelle'
    };
    return types[type] || type;
  };

  const getStatusLabel = (status) => {
    const statuses = {
      'draft': 'Brouillon',
      'sent': 'Envoyé pour signature',
      'signed': 'Signé',
      'archived': 'Archivé'
    };
    return statuses[status] || status;
  };

  return `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${getDocumentTypeLabel(document.documentType)} - ${client.firstName} ${client.lastName}</title>
    <style>
        @page {
            margin: 20mm;
            size: A4;
        }
        
        body {
            font-family: 'Arial', sans-serif;
            line-height: 1.6;
            color: #333;
            margin: 0;
            padding: 0;
            font-size: 12px;
        }
        
        .header {
            border-bottom: 2px solid #3b82f6;
            padding-bottom: 20px;
            margin-bottom: 30px;
        }
        
        .company-info {
            text-align: center;
            margin-bottom: 20px;
        }
        
        .company-name {
            font-size: 18px;
            font-weight: bold;
            color: #1e40af;
            margin-bottom: 5px;
        }
        
        .company-details {
            font-size: 11px;
            color: #6b7280;
            line-height: 1.4;
        }
        
        .document-title {
            text-align: center;
            font-size: 16px;
            font-weight: bold;
            margin: 30px 0;
            color: #1f2937;
        }
        
        .client-info {
            background: #f8fafc;
            padding: 15px;
            border-radius: 5px;
            margin-bottom: 20px;
            border-left: 4px solid #3b82f6;
        }
        
        .info-section {
            margin-bottom: 25px;
        }
        
        .info-section h3 {
            font-size: 14px;
            color: #374151;
            margin-bottom: 10px;
            border-bottom: 1px solid #e5e7eb;
            padding-bottom: 5px;
        }
        
        .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
        }
        
        .info-item {
            margin-bottom: 8px;
        }
        
        .info-label {
            font-weight: bold;
            color: #4b5563;
            margin-right: 5px;
        }
        
        .info-value {
            color: #6b7280;
        }
        
        .document-details {
            background: #f0f9ff;
            padding: 15px;
            border-radius: 5px;
            margin: 20px 0;
            border: 1px solid #bfdbfe;
        }
        
        .status-badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 15px;
            font-size: 10px;
            font-weight: bold;
            margin-left: 10px;
        }
        
        .status-draft { background: #f3f4f6; color: #374151; }
        .status-sent { background: #dbeafe; color: #1e40af; }
        .status-signed { background: #dcfce7; color: #166534; }
        .status-archived { background: #f3e8ff; color: #7c3aed; }
        
        .document-content {
            margin: 30px 0;
            line-height: 1.8;
        }
        
        .document-content h1, .document-content h2, .document-content h3 {
            color: #1e40af;
            margin-top: 25px;
            margin-bottom: 15px;
        }
        
        .document-content p {
            margin-bottom: 15px;
            text-align: justify;
        }
        
        .document-content ul, .document-content ol {
            margin-left: 20px;
            margin-bottom: 15px;
        }
        
        .document-content li {
            margin-bottom: 8px;
        }
        
        .footer {
            margin-top: 50px;
            padding-top: 20px;
            border-top: 1px solid #e5e7eb;
            text-align: center;
            font-size: 10px;
            color: #9ca3af;
        }
        
        .signature-area {
            margin-top: 40px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 40px;
        }
        
        .signature-box {
            border-top: 1px solid #d1d5db;
            padding-top: 10px;
            text-align: center;
            font-size: 11px;
            color: #6b7280;
        }
        
        .signature-date {
            font-size: 10px;
            color: #9ca3af;
            margin-top: 5px;
        }
        
        .watermark {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-45deg);
            font-size: 80px;
            color: rgba(0, 0, 0, 0.05);
            z-index: -1;
            font-weight: bold;
        }
        
        @media print {
            body {
                font-size: 11px;
            }
            
            .no-print {
                display: none;
            }
        }
    </style>
</head>
<body>
    <div class="watermark">${getDocumentTypeLabel(document.documentType)}</div>
    
    <div class="header">
        <div class="company-info">
            <div class="company-name">${companyInfo.name}</div>
            <div class="company-details">
                ${companyInfo.address}<br>
                Tél: ${companyInfo.phone} | Email: ${companyInfo.email}<br>
                ${companyInfo.rcs}${companyInfo.capital ? ` | Capital: ${companyInfo.capital}` : ''}
            </div>
        </div>
        
        <div class="document-title">
            ${getDocumentTypeLabel(document.documentType)}
            <span class="status-badge status-${document.status}">
                ${getStatusLabel(document.status)}
            </span>
        </div>
    </div>
    
    <div class="client-info">
        <div class="info-section">
            <h3>Informations du Bénéficiaire</h3>
            <div class="info-grid">
                <div class="info-item">
                    <span class="info-label">Nom:</span>
                    <span class="info-value">${client.firstName} ${client.lastName}</span>
                </div>
                <div class="info-item">
                    <span class="info-label">Adresse:</span>
                    <span class="info-value">${document.clientAddress || 'Non renseignée'}</span>
                </div>
                <div class="info-item">
                    <span class="info-label">Téléphone:</span>
                    <span class="info-value">${document.clientPhone || 'Non renseigné'}</span>
                </div>
                <div class="info-item">
                    <span class="info-label">Email:</span>
                    <span class="info-value">${document.clientEmail || 'Non renseigné'}</span>
                </div>
            </div>
        </div>
    </div>
    
    <div class="document-details">
        <div class="info-section">
            <h3>Détails du Document</h3>
            <div class="info-grid">
                <div class="info-item">
                    <span class="info-label">Type:</span>
                    <span class="info-value">${getDocumentTypeLabel(document.documentType)}</span>
                </div>
                <div class="info-item">
                    <span class="info-label">Statut:</span>
                    <span class="info-value">${getStatusLabel(document.status)}</span>
                </div>
                <div class="info-item">
                    <span class="info-label">Version:</span>
                    <span class="info-value">${document.documentVersion || '1.0'}</span>
                </div>
                <div class="info-item">
                    <span class="info-label">Date de création:</span>
                    <span class="info-value">${document.metadata?.createdAt?.toDate?.()?.toLocaleDateString('fr-FR') || 'Date inconnue'}</span>
                </div>
                <div class="info-item">
                    <span class="info-label">Référence:</span>
                    <span class="info-value">${document.id || 'N/A'}</span>
                </div>
            </div>
        </div>
    </div>
    
    <div class="document-content">
        ${document.content || `
        <p>
            Ce document représente le ${getDocumentTypeLabel(document.documentType).toLowerCase()} 
            établi entre ${companyInfo.name} et ${client.firstName} ${client.lastName}.
            ${document.status === 'signed' ? 'Le document a été signé électroniquement.' : ''}
            ${document.status === 'sent' ? 'Le document est en attente de signature.' : ''}
            ${document.status === 'draft' ? 'Ce document est en cours de rédaction.' : ''}
        </p>
        
        <p>
            <strong>Important:</strong> Ce document a été généré automatiquement par le système 
            de gestion OMAD Services. Pour toute question concernant ce document, veuillez 
            contacter ${companyInfo.name} au ${companyInfo.phone}.
        </p>
        `}
    </div>
    
    ${document.status === 'signed' && document.signature ? `
    <div class="signature-area">
        <div class="signature-box">
            Signature du Bénéficiaire<br>
            ${document.signature.clientName || `${client.firstName} ${client.lastName}`}<br>
            <span class="signature-date">
                Signé le ${document.signature.signatureDate?.toDate?.()?.toLocaleDateString('fr-FR') || 'Date inconnue'}
            </span>
        </div>
        <div class="signature-box">
            Signature de ${companyInfo.name}<br>
            Représentant légal<br>
            <span class="signature-date">
                Signé le ${document.metadata?.signedAt?.toDate?.()?.toLocaleDateString('fr-FR') || 'Date inconnue'}
            </span>
        </div>
    </div>
    ` : `
    <div class="signature-area">
        <div class="signature-box">
            Signature du Bénéficiaire<br>
            ${client.firstName} ${client.lastName}
        </div>
        <div class="signature-box">
            Signature de ${companyInfo.name}<br>
            Représentant légal
        </div>
    </div>
    `}
    
    <div class="footer">
        Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')} | 
        ${companyInfo.name} - ${companyInfo.rcs} | 
        Ce document est confidentiel et destiné uniquement aux parties concernées.
    </div>
</body>
</html>
  `;
};

/**
 * Génère et télécharge un PDF pour un document légal
 * @param {Object} document - Le document légal
 * @param {Object} client - Les informations du client
 */
export const genererPDFDocumentLegal = async (document, client) => {
  try {
    const htmlContent = genererHTMLDocumentLegal(document, client);
    
    const options = {
      margin: 10,
      filename: `Document_Legal_${document.documentType}_${client.firstName}_${client.lastName}_${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    
    await html2pdf().from(htmlContent).set(options).save();
    
  } catch (error) {
    console.error('Erreur lors de la génération du PDF du document légal:', error);
    throw new Error('Erreur lors de la génération du PDF: ' + error.message);
  }
};

/**
 * Ouvre le document dans une nouvelle fenêtre pour impression
 * @param {Object} document - Le document légal
 * @param {Object} client - Les informations du client
 */
export const imprimerDocumentLegal = async (document, client) => {
  try {
    const htmlContent = genererHTMLDocumentLegal(document, client);
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    printWindow.onload = () => {
      printWindow.print();
    };
    
  } catch (error) {
    console.error('Erreur lors de l\'impression du document légal:', error);
    throw new Error('Erreur lors de l\'impression: ' + error.message);
  }
};

/**
 * Génère l'URL d'un PDF pour prévisualisation
 * @param {Object} document - Le document légal
 * @param {Object} client - Les informations du client
 * @returns {Promise<string>} URL du PDF
 */
export const genererURLPDFDocumentLegal = async (document, client) => {
  try {
    const htmlContent = genererHTMLDocumentLegal(document, client);
    
    const options = {
      margin: 10,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    
    const pdf = await html2pdf().from(htmlContent).set(options).output('blob');
    return URL.createObjectURL(pdf);
    
  } catch (error) {
    console.error('Erreur lors de la génération de l\'URL PDF:', error);
    throw new Error('Erreur lors de la génération de l\'URL PDF: ' + error.message);
  }
};
